import { ITextStyles, getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { Info } from 'components/Notification/Info';
import { downArrowIcon, globalFontBoldWeight, globalStackTokensGapExtraSmall, upArrowIcon } from 'globalStyles';
import React, { useContext, useEffect, useState } from 'react';
import { darkTheme, lightTheme } from 'themes/runtime/globalThemes-v8';
import { LocalStorageKeys, getLocalStorageData, setLocalStorageData } from 'utils/localstorage';
import { toBool } from 'utils/string';

interface IAccordionProps {
  className?: string;
  defaultActive?: boolean;
  setActiveKey?: string;
  active?: boolean;
  title: string;
  disabled?: boolean;
  showBorder?: boolean;
  cacheKey?: LocalStorageKeys;
  children: React.ReactNode;
  disableFocus?: boolean;
  onToggle?: (expanded: boolean) => void;
  summary?: string; //show summary when accordion is collapsed
  info?: string; //show (i) info when accordion is expanded
  maxHeight?: number | string;
}

const Accordion = (props: IAccordionProps) => {
  const getDefaultState = (): boolean => {
    //1. When cacheKey is provided, try to load the state from local cache
    //2. If this fails or not provided, use defaultActive
    //3. If not provied, set to false
    let defState: boolean = false;

    if (props.cacheKey) {
      try {
        const cachedState = getLocalStorageData(appContext, props.cacheKey);
        defState = toBool(cachedState);
      } catch {
        if (props.defaultActive) defState = props.defaultActive;
      }
    } else if (props.defaultActive) {
      defState = props.defaultActive;
    }

    return defState;
  };

  const saveState = () => {
    if (props.cacheKey) {
      try {
        setLocalStorageData(appContext, props.cacheKey, activeState.toString());
      } catch {
        //ignore
      }
    }
  };

  const [activeState, setActiveState] = useState<boolean>(getDefaultState());
  const [firstTimeLoad, setFirstTimeLoad] = useState<boolean>(false);
  const appContext = useContext(AppContext);
  const theme = getTheme();
  const maxHeight = props.maxHeight ? props.maxHeight : 2000;

  useEffect(() => {
    if (props.setActiveKey) {
      setActiveState(true);
    }
  }, [props.setActiveKey]);

  useEffect(() => {
    if (!firstTimeLoad) {
      saveState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeState]);

  useEffect(() => {
    if (props.disabled) {
      setActiveState(false);
    }
  }, [props.disabled]);

  useEffect(() => {
    if (props.active !== undefined) {
      if (props.defaultActive !== undefined && !firstTimeLoad) {
        setFirstTimeLoad(true);
      } else {
        setActiveState(props.active);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.active]);

  const getTextStylesTitle = (disabled: boolean): ITextStyles => {
    return {
      root: {
        fontWeight: globalFontBoldWeight,
        color: disabled
          ? appContext.useDarkMode
            ? darkTheme.palette?.neutralLighter
            : lightTheme.palette?.neutralQuaternary
          : undefined,
      },
    };
  };

  const getTextStylesSummary = (disabled: boolean): ITextStyles => {
    return {
      root: {
        color: disabled
          ? appContext.useDarkMode
            ? darkTheme.palette?.neutralLighter
            : lightTheme.palette?.neutralQuaternary
          : undefined,
        paddingRight: 10,
      },
    };
  };

  const toggleAccordion = () => {
    if (!props.disabled) {
      setActiveState(!activeState);
      if (props.onToggle && !firstTimeLoad) {
        props.onToggle(!activeState);
      }
    }
  };

  const getTitle = (): string => {
    return props.title;
  };

  const getSummary = (): string => {
    if (!props.summary) {
      return '';
    } else {
      return ' (' + props.summary + ')';
    }
  };

  //
  // Main render
  //
  return (
    <IPStack
      onFocusCapture={() => {
        if (!props.disableFocus && !activeState) {
          toggleAccordion();
        }
      }}
    >
      <IPStack.Item>
        <IPStack
          className={props.className}
          onClick={toggleAccordion}
          horizontal
          horizontalAlign="space-between"
          styles={{
            root: {
              background: appContext.useDarkMode ? theme.palette.neutralPrimaryAlt : theme.palette.themeLighterAlt,
              borderRadius: props.showBorder ? '3px' : undefined,
              border: props.showBorder ? `1px solid ${theme.palette.neutralLight}` : undefined,
              cursor: 'pointer',
              height: 36,
              padding: 7,
            },
          }}
        >
          <IPStack
            horizontal
            styles={{ root: { overflow: 'hidden', paddingRight: 10 } }}
            tokens={globalStackTokensGapExtraSmall}
            verticalAlign="center"
          >
            <IPStack.Item>
              <IPText nowrap styles={getTextStylesTitle(props.disabled ?? false)}>
                {getTitle()}
              </IPText>
            </IPStack.Item>
            {!activeState && (
              <IPStack.Item>
                <IPText block nowrap styles={getTextStylesSummary(props.disabled ?? false)}>
                  {getSummary()}
                </IPText>
              </IPStack.Item>
            )}
            {activeState && props.info && (
              <IPStack.Item>
                <Info message={props.info} />
              </IPStack.Item>
            )}
          </IPStack>
          <IPStack.Item>
            {activeState ? <IPFontIcon {...downArrowIcon} /> : <IPFontIcon {...upArrowIcon} />}
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item
        grow
        styles={{
          root: {
            borderRadius: props.showBorder ? '3px' : undefined,
            borderLeft: activeState && props.showBorder ? `1px solid ${theme.palette.neutralLight}` : undefined,
            borderRight: activeState && props.showBorder ? `1px solid ${theme.palette.neutralLight}` : undefined,
            borderBottom: activeState && props.showBorder ? `1px solid ${theme.palette.neutralLight}` : undefined,
            maxHeight: activeState ? maxHeight : 0,
            transition: activeState ? 'max-height 0.7s ease-in' : undefined,
            overflow: 'hidden',
          },
        }}
      >
        {props.children}
      </IPStack.Item>
    </IPStack>
  );
};

export default Accordion;
